(function() {

    // Factory for the Alpine component
    const factory = (config = {}) => ({

        /** Registry of checkbox nodes */
        nodes: {},

        /** Active selections (IDs; array so x-model works) */
        selected: [],

        /** Injected config maps */
        descendantMap: config.descendantMap || {},
        descendantKeyMap: config.descendantKeyMap || {},
        descendantSummaryMap: config.descendantSummaryMap || {},

        /**
         * testIdentKeyMap: { "<id>": "<key>" }
         * Example: { "2205": "vitd" }
         */
        testIdentKeyMap: config.testIdentKeyMap || {},

        /**
         * capacityAvailable:
         * {
         *   "2": { name: "Trockenblut", available: 100, required: 0 },
         *   "4": { name: "Kapillarblut", available: 100, required: 0 }
         * }
         */
        capacityAvailable: JSON.parse(
            JSON.stringify(config.capacityAvailable || {})),

        /**
         * capacityRequired:
         * {
         *   638: { 4: 1.5 },
         *   180: { 2: 1.0 },
         *   ...
         * }
         */
        capacityRequired: config.capacityRequired || {},

        userInteracted: false,

        /** Tracks last user toggled ID to resolve duplicates deterministically */
        lastToggledId: null,

        requiredSampleMaterial: null,

        /** ----- BADGE FILTER (SLUGS) ----- */

        /**
         * Active badge filter slugs (array so Alpine updates reliably)
         * Example: ["vitd", "omega3"]
         */
        activeSlugs: Array.isArray(config.activeSlugs) ? config.activeSlugs.map(String) : [],

        /**
         * Filter mode: "or" (any match) or "and" (must match all)
         */
        filterMode: ('and' === String(config.filterMode || '').toLowerCase()) ? 'and' : 'or',

        showOnlyFeaturedGroupOnStart: true === config.showOnlyFeaturedGroupOnStart,
        showAllEntries: false,

        dispatchActiveSlugsUpdated() {
            window.dispatchEvent(new CustomEvent('test-ident-active-slugs-updated'));
        },

        toggleSlug(slug) {

            slug = String(slug);
            this.showAllEntries = false;

            if (true === this.activeSlugs.includes(slug)) {
                this.activeSlugs = this.activeSlugs.filter(v => v !== slug);
                this.dispatchActiveSlugsUpdated();
                return;
            }

            this.activeSlugs.push(slug);
            this.dispatchActiveSlugsUpdated();
        },

        showAllTestIdents() {
            this.showAllEntries = true;
            this.clearSlugs();
        },

        isSlugActive(slug) {
            return true === this.activeSlugs.includes(String(slug));
        },

        clearSlugs() {
            this.activeSlugs = [];
            this.dispatchActiveSlugsUpdated();
        },

        /**
         * Decide if an item with given slugs should be visible
         * - If 0 active slugs -> show all
         * - OR: itemSlugs intersects activeSlugs
         * - AND: itemSlugs contains all activeSlugs
         */
        matchesSlugs(itemSlugs) {

            if (0 === this.activeSlugs.length) {
                return true;
            }

            itemSlugs = Array.isArray(itemSlugs) ? itemSlugs.map(String) : [];

            if ('and' === this.filterMode) {
                return this.activeSlugs.every(s => true === itemSlugs.includes(String(s)));
            }

            // Default: OR
            return itemSlugs.some(s => true === this.activeSlugs.includes(String(s)));
        },

        shouldShowBySlugs(itemSlugs, groupIndex = null) {

            if (0 < this.activeSlugs.length) {
                return this.matchesSlugs(itemSlugs);
            }

            if (true === this.showAllEntries) {
                return true;
            }

            if (true === this.showOnlyFeaturedGroupOnStart) {
                return 0 === Number(groupIndex);
            }

            return this.matchesSlugs(itemSlugs);
        },

        /**
         * Read slugs from a DOM element:
         * <div data-slugs="vitd,omega3"></div>
         */
        matchesElementByDataSlugs(el) {

            if (!el) {
                return true;
            }

            const raw = String(el.dataset?.slugs || '').trim();

            const itemSlugs = '' === raw
                ? []
                : raw.split(',').map(v => v.trim()).filter(v => '' !== v);

            return this.matchesSlugs(itemSlugs);
        },

        /** ----- EXISTING INIT/WATCHERS ----- */

        initGlobal() {

            this.$watch('selectedCsv', value => {

                const input = document.getElementById(
                    'selected-test-idents-csv');

                if (!input) {
                    return;
                }

                if (input.value !== value) {
                    input.value = value;
                    input.dispatchEvent(new Event('input', {bubbles: true}));
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                }

                // this.$wire.set('data.extra_attributes.selected_test_idents_csv', value);
            });

            this.$watch('requiredSampleMaterial', value => {

                const input = document.getElementById(
                    'required-sample-material');

                if (!input) {
                    return;
                }

                if (input.value !== value) {
                    input.value = value;
                    input.dispatchEvent(new Event('input', {bubbles: true}));
                    input.dispatchEvent(new Event('change', {bubbles: true}));
                }

                // this.$wire.set('data.extra_attributes.required_sample_material',
                //     value);
            });
        },

        get missingSelection() {
            return true === this.userInteracted && 0 === this.selected.length;
        },

        get selectedCsv() {
            return this.selected.join(',');
        },

        set selectedCsv(val) {
            this.selected = val
                ? val.split(',').map(v => v.trim()).filter(v => '' !== v)
                : [];
        },

        /** Selection helpers */

        isSelected(key) {
            return true === this.selected.includes(String(key));
        },

        addSelected(key) {
            key = String(key);
            if (false === this.isSelected(key)) {
                this.selected.push(key);
            }
        },

        removeSelected(key) {
            key = String(key);
            this.selected = this.selected.filter(v => v !== key);
        },

        removeSelectedOption(key) {

            key = String(key);

            const node = this.nodes[key];

            if (!node) {
                this.removeSelected(key);
                this.normalize();
                this.recomputeCapacities();
                this.updateMaterialBars();
                this.updateCapacityDisabledState();
                this.updateRequiredSampleMaterial();
                return;
            }

            if (true === node.preselected) {
                return;
            }

            node.el.checked = false;
            this.toggle(key);
        },

        /**
         * Map an ID to its semantic key (e.g. "vitd").
         * Fallback: return the ID itself if mapping missing.
         */
        toKeyById(id) {
            id = String(id);

            const key = this.testIdentKeyMap?.[id];

            return (typeof key === 'string' && '' !== key)
                ? String(key)
                : id;
        },

        /**
         * Return all node IDs that represent the given semantic key.
         * This covers cases where the same test exists with multiple IDs.
         */
        getNodeIdsByKey(targetKey) {
            targetKey = String(targetKey);

            const ids = [];
            for (const id in this.nodes) {
                if (this.toKeyById(id) === targetKey) {
                    ids.push(String(id));
                }
            }

            return ids;
        },

        /**
         * Register checkbox
         */
        register(key, el, descendants, isPreselected = false) {

            key = String(key);

            this.nodes[key] = {
                el,
                descendants: Array.isArray(descendants) ? descendants.map(
                    String) : [],
                preselected: !!isPreselected,
            };

            const wrapper = el.closest('.button-option');

            if (true === isPreselected) {
                // Preselected nodes stay fixed
                el.checked = true;
                el.disabled = true;

                if (wrapper) {
                    wrapper.classList.add('disabled', 'preselected', 'selected');
                }

                this.addSelected(key);

            } else if (true === el.checked) {
                this.addSelected(key);
            }

            this.normalize();
            this.recomputeCapacities();
            this.updateMaterialBars();
            this.updateCapacityDisabledState();
            this.updateRequiredSampleMaterial();
        },

        /**
         * Handle user interaction
         */
        toggle(key) {

            this.userInteracted = true;

            key = String(key);
            this.lastToggledId = key;

            const node = this.nodes[key];
            if (!node) {
                return;
            }

            // Preselected nodes are fixed
            if (true === node.preselected) {
                node.el.checked = true;
                return;
            }

            if (true === node.el.checked) {
                this.addSelected(key);
            } else {
                this.removeSelected(key);
            }

            this.normalize();
            this.recomputeCapacities();
            this.updateMaterialBars();
            this.updateCapacityDisabledState();
            this.updateRequiredSampleMaterial();
        },

        /**
         * Graph + overlap normalization:
         * - Enforce uniqueness by semantic key (e.g. "vitd")
         * - Remove selected children if parent selected (graph descendants by ID)
         * - Reset disabled state
         * - Disable graph descendants (by ID)
         * - Apply overlap/exclusion via descendantKeyMap (KEYS!)
         * - Never remove or modify preselected items
         */
        normalize() {

            const snapshot = [...this.selected];

            /**
             * Step 0: enforce uniqueness by semantic key.
             * Keep exactly one ID per key.
             *
             * Priority:
             * 1) preselected
             * 2) last toggled
             * 3) first seen
             */
            const chosenByKey = new Map(); // key -> id

            for (const id of snapshot) {

                const key = this.toKeyById(id);

                if (false === chosenByKey.has(key)) {
                    chosenByKey.set(key, id);
                    continue;
                }

                const currentId = chosenByKey.get(key);
                const currentNode = this.nodes[currentId];
                const candidateNode = this.nodes[id];

                // Preselected wins
                if (true === (candidateNode?.preselected ?? false) && false ===
                    (currentNode?.preselected ?? false)) {
                    chosenByKey.set(key, id);
                    continue;
                }

                // Otherwise: keep last toggled if it matches
                if (
                    null !== this.lastToggledId
                    && String(id) === String(this.lastToggledId)
                    && false === (candidateNode?.preselected ?? false)
                ) {
                    chosenByKey.set(key, id);
                }
            }

            // Remove non-chosen duplicates
            for (const id of snapshot) {

                const key = this.toKeyById(id);
                const keepId = chosenByKey.get(key);

                if (String(id) === String(keepId)) {
                    continue;
                }

                const node = this.nodes[id];
                if (node && false === node.preselected) {

                    node.el.checked = false;
                    node.el.dispatchEvent(new Event('input', {bubbles: true}));
                    node.el.dispatchEvent(new Event('change', {bubbles: true}));

                    this.removeSelected(id);
                }
            }

            /**
             * Step 1: remove child selections if a parent is selected
             */
            const snapshotAfterDedupe = [...this.selected];

            for (const a of snapshotAfterDedupe) {
                for (const b of snapshotAfterDedupe) {

                    if (a === b) {
                        continue;
                    }

                    const bDesc = this.nodes[b]?.descendants || [];
                    if (true === bDesc.includes(a)) {

                        const nodeA = this.nodes[a];

                        if (nodeA && false === nodeA.preselected) {

                            nodeA.el.checked = false;
                            nodeA.el.dispatchEvent(
                                new Event('input', {bubbles: true}));
                            nodeA.el.dispatchEvent(
                                new Event('change', {bubbles: true}));

                            this.removeSelected(a);
                        }
                    }
                }
            }

            /**
             * Step 2: reset all nodes
             */
            Object.values(this.nodes).forEach(n => {
                if (true === n.preselected) {
                    return;
                }
                this.toggleNode(n, false);
            });

            /**
             * Step 3: disable graph descendants
             */
            for (const parent of this.selected) {

                const desc = this.nodes[parent]?.descendants || [];

                for (const childId of desc) {

                    const child = this.nodes[childId];
                    if (!child || true === child.preselected) {
                        continue;
                    }

                    child.el.checked = false;
                    this.removeSelected(childId);

                    this.toggleNode(child, true, true);
                }
            }

            /**
             * Step 4: apply overlap/exclusion via descendantKeyMap (KEYS!)
             *
             * descendantKeyMap[parentId] = ["holotc","vitd","feri",...]
             * For each excluded key: disable all node IDs that have that key.
             */
            const excludedKeys = new Set();

            for (const parentId of this.selected) {

                const keys = this.descendantKeyMap?.[String(parentId)] || [];
                if (false === Array.isArray(keys)) {
                    continue;
                }

                for (const k of keys) {
                    if ('string' === typeof k && '' !== k) {
                        excludedKeys.add(
                            String(k));
                    }
                }
            }

            for (const exKey of excludedKeys) {

                // Prevent self-lock if a mapping ever includes the same key as selected
                const selectedHasSameKey = this.selected.some(
                    id => this.toKeyById(id) === exKey);

                if (true === selectedHasSameKey) {
                    continue;
                }

                const ids = this.getNodeIdsByKey(exKey);

                for (const id of ids) {

                    const node = this.nodes[id];
                    if (!node || true === node.preselected) {
                        continue;
                    }

                    node.el.checked = false;
                    this.removeSelected(id);

                    this.toggleNode(node, true, true);
                }
            }

            /**
             * Step 4b: disable "panel" nodes whose descendantKeyMap is fully covered
             * by excluded keys (e.g. selecting 2205 disables 2509 if 2509 == [trig,ldl,hdl,CHOL]).
             */
            for (let descendantId in this.descendantKeyMap) {

                descendantId = String(descendantId);

                if (this.lastToggledId === descendantId) {
                    continue;
                }

                const candidateNode = this.nodes?.[descendantId];

                if (!candidateNode) {
                    continue;
                }

                if (true === (candidateNode.preselected ?? false)) {
                    continue;
                }

                // // Never disable currently selected items here (handled by the toggle flow)
                if (true === this.isSelected(descendantId)) {
                    continue;
                }

                const candidateKeys = this.descendantKeyMap?.[descendantId];

                if (false === Array.isArray(candidateKeys) || 0 === candidateKeys.length) {
                    continue;
                }

                // Candidate is considered blocked if ALL its keys are excluded
                let isFullyExcluded = true;

                for (const k of candidateKeys) {

                    if ('string' !== typeof k || '' === k) {
                        continue;
                    }

                    if (false === excludedKeys.has(String(k))) {
                        isFullyExcluded = false;
                        break;
                    }
                }

                if (false === isFullyExcluded) {
                    continue;
                }

                // If candidate got selected somehow before, force unselect (unless preselected already skipped)
                candidateNode.el.checked = false;
                this.removeSelected(descendantId);

                this.toggleNode(candidateNode, true, true);
            }
        },

        /** Disable / enable one node (input + wrapper styling) */
        toggleNode(node, disabled, selected = false) {

            // Preselected nodes always stay disabled and styled
            if (true === node.preselected) {

                node.el.disabled = true;

                const wrapper = node.el.closest('.button-option');

                if (wrapper) {
                    wrapper.classList.add('disabled', 'preselected');
                }

                return;
            }

            node.el.disabled = disabled;

            const wrapper = node.el.closest('.button-option');

            if (wrapper) {

                wrapper.classList.remove('selected');
                wrapper.classList.toggle('disabled', disabled);

                if (true === selected || node.el.checked) {
                    wrapper.classList.add('selected');
                }
            }
        },

        /** ----- MATERIAL CALCULATION ----- */

        recomputeCapacities() {

            // Reset all required values
            for (const id in this.capacityAvailable) {
                this.capacityAvailable[id].required = 0;
            }

            // Sum requirements for selected TestIdents
            for (const testId of this.selected) {

                const req = this.capacityRequired[testId] || {};

                for (const matId in req) {

                    const amount = parseFloat(req[matId]) || 0;
                    if (!this.capacityAvailable[matId]) {
                        continue;
                    }

                    this.capacityAvailable[matId].required += amount;
                }
            }

            // Clamp
            for (const id in this.capacityAvailable) {

                const cap = this.capacityAvailable[id];

                if (cap.required < 0) {
                    cap.required = 0;
                }

                if (cap.available > 0 && cap.required > cap.available) {
                    cap.required = cap.available;
                }
            }
        },

        updateMaterialBars() {

            const container = document.querySelector('.sample-material-info');
            if (!container) {
                return;
            }

            container.querySelectorAll('[data-material-id]').forEach(row => {

                const materialId = row.getAttribute('data-material-id');
                const mat = this.capacityAvailable[materialId];
                if (!mat) {
                    return;
                }

                const percent = mat.available > 0
                    ? Math.min((mat.required * 100) / mat.available, 100)
                    : 0;

                const bar = row.querySelector('.sample-bar-fill');
                if (bar) {
                    bar.style.width = percent + '%';
                }

                row.querySelectorAll('[data-seg]').forEach(seg => {

                    const index = Number(seg.getAttribute('data-seg'));

                    seg.classList.toggle('text-white', index <= mat.required);
                    seg.classList.toggle('text-gray-500', index > mat.required);
                });
            });
        },

        isOptionAllowedByCapacity(key) {

            key = String(key);

            const req = this.capacityRequired[key];
            if (!req) {
                return true;
            }

            for (const matId in req) {

                const needed = parseFloat(req[matId]) || 0;
                const mat = this.capacityAvailable[matId];
                if (!mat) {
                    continue;
                }

                const remaining = mat.available - mat.required;

                if (remaining < needed) {
                    return false;
                }
            }

            return true;
        },

        updateCapacityDisabledState() {

            for (const key in this.nodes) {

                const node = this.nodes[key];

                // Preselected nodes always disabled & styled
                if (true === node.preselected) {
                    node.el.checked = true;
                    this.toggleNode(node, true);
                    continue;
                }

                // Selected items must stay enabled
                if (true === this.isSelected(key)) {
                    this.toggleNode(node, false);
                    continue;
                }

                // If already disabled by graph logic → do not override
                if (true === node.el.disabled) {
                    continue;
                }

                const allowed = this.isOptionAllowedByCapacity(key);
                this.toggleNode(node, !allowed);
            }
        },

        updateRequiredSampleMaterial() {

            // Track which material IDs are needed
            const requiredMaterialIds = new Set();

            // Accumulate from the selected test idents
            for (const testId of this.selected) {

                const reqCaps = this.capacityRequired[testId] || {};

                for (const matId in reqCaps) {
                    requiredMaterialIds.add(String(matId));
                }
            }

            this.requiredSampleMaterial = [...requiredMaterialIds].sort().
                join('');
        },

    });

    // Safe registration
    if (window.Alpine) {
        window.Alpine.data('testIdentSelectionGlobal', factory);
    } else {
        document.addEventListener('alpine:init', () => {
            window.Alpine.data('testIdentSelectionGlobal', factory);
        });
    }
})();
